import { Injectable, OnDestroy, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import { AuthSessionService } from '@core/auth/services/auth-session.service';
import { APP_CONSTANTS } from '@core/constants/app-constants';
import { HttpClientService } from '@core/interceptors';
import { LoggerService } from '@core/services/logger.service';

interface SessionPropertiesResponse {
  result?: {
    session_timeout?: number;
  };
}

@Injectable({ providedIn: 'root' })
export class IdleSessionService implements OnDestroy {
  private readonly authSession = inject(AuthSessionService);
  private readonly http = inject(HttpClientService);
  private readonly logger = inject(LoggerService);
  private readonly activityEvents: readonly (keyof WindowEventMap)[] = [
    'pointerdown',
    'keydown',
    'scroll',
    'touchstart',
  ];
  private readonly handleActivity = () => this.scheduleLogout();
  private timeoutId: number | null = null;
  private started = false;
  // Server-configured timeout (ms), refreshed in the background; falls back to the constant.
  private idleTimeoutMs = APP_CONSTANTS.session.IDLE_TIMEOUT_MS;

  start(): void {
    if (this.started || typeof window === 'undefined') {
      return;
    }

    this.started = true;
    for (const eventName of this.activityEvents) {
      window.addEventListener(eventName, this.handleActivity, {
        capture: true,
        passive: true,
      });
    }
    this.scheduleLogout();
    void this.loadIdleTimeoutFromServer();
  }

  ngOnDestroy(): void {
    if (typeof window !== 'undefined') {
      for (const eventName of this.activityEvents) {
        window.removeEventListener(eventName, this.handleActivity, true);
      }
    }

    this.clearTimer();
    this.started = false;
  }

  private scheduleLogout(): void {
    this.clearTimer();
    this.timeoutId = window.setTimeout(
      () => void this.logoutIfSessionIsActive(),
      this.idleTimeoutMs,
    );
  }

  private clearTimer(): void {
    if (this.timeoutId === null || typeof window === 'undefined') {
      return;
    }

    window.clearTimeout(this.timeoutId);
    this.timeoutId = null;
  }

  private async logoutIfSessionIsActive(): Promise<void> {
    this.timeoutId = null;
    if (!this.authSession.isAuthenticated() && !this.authSession.hasImpersonatedSession()) {
      this.scheduleLogout();
      return;
    }

    try {
      await this.authSession.logout();
    } catch {
      this.logger.error('Automatic logout failed after session inactivity');
    }
  }

  // Refreshes the idle timeout from the properties API without blocking startup or activity tracking.
  private async loadIdleTimeoutFromServer(): Promise<void> {
    try {
      const response = await firstValueFrom(
        this.http.get<SessionPropertiesResponse>(
          APP_CONSTANTS.session.SESSION_PROPERTIES_ENDPOINT,
          { errorNotification: { enabled: false } },
        ),
      );

      const sessionTimeoutMinutes = response?.result?.session_timeout;
      if (
        typeof sessionTimeoutMinutes !== 'number' ||
        !Number.isFinite(sessionTimeoutMinutes) ||
        sessionTimeoutMinutes <= 0
      ) {
        return;
      }

      this.idleTimeoutMs = sessionTimeoutMinutes * 60 * 1000;
      if (this.started) {
        this.scheduleLogout();
      }
    } catch {
      // Keep the APP_CONSTANTS.session.IDLE_TIMEOUT_MS fallback when the properties API fails.
    }
  }
}
